import { ArrowRight, Eye, Sparkles, Bot } from "lucide-react";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Badge,
  Checkbox,
  Progress,
} from "@ncoder/ui";

interface ComponentPreviewScreenProps {
  componentSelection: any[];
  setComponentSelection: React.Dispatch<React.SetStateAction<any[]>>;
  setCurrentScreen: React.Dispatch<
    React.SetStateAction<"tools" | "preview" | "results">
  >;
  isAnalyzing: boolean;
  analysisProgress: number;
  handleStartModernization: () => void;
}

export const ComponentPreviewScreen: React.FC<ComponentPreviewScreenProps> = ({
  componentSelection,
  setComponentSelection,
  setCurrentScreen,
  isAnalyzing,
  analysisProgress,
  handleStartModernization,
}) => {
  const selectedCount = componentSelection.filter(
    (comp) => comp.selected
  ).length;
  const allSelected =
    componentSelection.length > 0 &&
    componentSelection.every((comp) => comp.selected);

  const handleComponentToggle = (id: string) => {
    setComponentSelection((prev) =>
      prev.map((c) => (c.id === id ? { ...c, selected: !c.selected } : c))
    );
  };

  const handleSelectAll = (checked: boolean) => {
    setComponentSelection((prev) =>
      prev.map((c) => ({ ...c, selected: checked }))
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-6 max-w-7xl">
        {/* ---------- Header ---------- */}
        <div className="flex items-center justify-between mb-8">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg flex items-center justify-center">
                <Eye className="h-5 w-5 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold">Component Preview</h1>
                <p className="text-muted-foreground">
                  Review and select components to modernize
                </p>
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setCurrentScreen("tools")}>
              <ArrowRight className="h-4 w-4 mr-2 rotate-180" />
              Back to Tools
            </Button>
            {selectedCount > 0 && (
              <Button onClick={handleStartModernization}>
                <Sparkles className="h-4 w-4 mr-2" />
                Modernize ({selectedCount})
              </Button>
            )}
          </div>
        </div>

        {/* ---------- Component Selection ---------- */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Components Found</CardTitle>
                <CardDescription>
                  {componentSelection.length} components available for
                  modernization
                </CardDescription>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="select-all"
                  checked={allSelected}
                  onCheckedChange={(checked) => handleSelectAll(!!checked)}
                />
                <label htmlFor="select-all" className="text-sm font-medium">
                  Select All
                </label>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {componentSelection.map((component) => (
                <div
                  key={component.id}
                  className="flex items-center space-x-3 p-3 border rounded-lg"
                >
                  <Checkbox
                    checked={component.selected}
                    onCheckedChange={() => handleComponentToggle(component.id)}
                  />
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium">{component.name}</h4>
                      <Badge variant="outline">{component.type}</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      {component.details}
                    </p>
                    <div className="flex items-center gap-2 mt-2">
                      <Badge variant="secondary" className="text-xs">
                        {component.status}
                      </Badge>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* ---------- Modernization Progress ---------- */}
        {isAnalyzing && (
          <Card className="mt-6">
            <CardContent className="pt-6">
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Bot className="h-5 w-5 animate-pulse" />
                  <span>AI modernizing selected components...</span>
                </div>
                <Progress value={analysisProgress} className="w-full" />
                <div className="text-sm text-muted-foreground text-center">
                  {analysisProgress}% complete
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};
